/*
 * Hyper link component
 */
import React from 'react';

import Base from './base.jsx';
import {
  castDataType,
  DataType
} from '../common.jsx'
import Setting from "./setting.jsx";
import Helpers from './helpers.jsx';
import RiskMatrix from '../../risk_management/RiskMatrix.jsx'; // TBD: LAZY LOAD?
import ProjectResourceMonitoring from '../../project_resource/ProjectResourceMonitoring.jsx';
import ProjectPhasesIndex from '../../phase_schemas/project/ProjectPhasesIndex.jsx';
import WorkLogCondensedView from '../../work_log/WorkLogCondensedView.jsx';

import {
  risk_management_monitoring_path,
  project_resource_monitoring_path,
  monitoring_deliverables_module_path,
  project_monitoring_worklogs_path,
} from '~/routes'

import { FaIcon } from '../../common/Icons.jsx';
import TooltipWithRef from '../../common/Tooltip.jsx';

const Placeholder = () => <h1>WIP</h1> //UNTIL IMPLEMENTED

const EXTERNAL_TOOL_MANIFEST = {
  risks: {
    path: (projectId) => { return risk_management_monitoring_path({project_id: projectId}) }, 
    Component: RiskMatrix,
    defaultProps: {
      section: '' ,
      filterModel: {},
      setFilterModel: () => {},
      detailedView: false,
      renderGauge: false
    }
  },
  phases: {
    path: (projectId) => { return monitoring_deliverables_module_path({project_id: projectId}) },
    Component: ProjectPhasesIndex,
    defaultProps: {}
  },
  resources: {
    path: (projectId) => { return project_resource_monitoring_path({project_id: projectId}) }, 
    Component: ProjectResourceMonitoring,
    defaultProps: {
      viewMode: true,
      initialGroupBy: 'person',
      height: 600
    }
  },
  budget: {
    path: (project) => { }, 
    Component: Placeholder,
    defaultProps: {}
  },
  tasks: {
    path: (project) => { }, 
    Component: Placeholder,
    defaultProps: {}
  },
  work_logs: {
    path: (projectId) => { return project_monitoring_worklogs_path({project_id: projectId}) },
    Component: WorkLogCondensedView,
    defaultProps: {}
  },
}


const FieldWrapper = ({label_hidden, children, self, link}) => {
 return <div>
     <label className={label_hidden ? 'hidden-label': ''}>
        <span className="field-label">{self.getText('label')}</span>
        {link}
      </label>
      {children}
  </div>
}

const Alert = ({level, children}) => <div style={{textAlign: 'center'}} className={`alert alert-${level}`}>{children}</div>

export default class ExternalTool extends Base {

  static get ANSWER_DEPENDENT() {
    return true
  }

  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
    if (content?.tool === 'work_logs' && content?.periodFieldName) {
      return [`[${content.periodFieldName}]`];
    }
    return [];
  }

  static get CATEGORY() {
    return 'other';
  }

  static get TYPE() {
    return 'external_tool';
  }

  static icon() {
    return <i className="fa fa-arrow-up-right-from-square"></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_link_label',
      description: 'field_default_description',
      text: null
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      label: 'label',
      text: 'text',
      url: '',
      description: 'description',
      guideline: false,
      prettyView: {
        hide: false,
        show_label: false
      },
      tool: 'risks',
      periodFieldName: ''
    });
  }

  // Only monthly periods are supported for now
  parsePeriodAnswer(answer) {
    if (!answer) return { year: null, month: null };
    const [yearStr, subPart] = answer.split('_');
    const year = parseInt(yearStr);
    if (!subPart || !subPart.startsWith('M')) return { year: null, month: null };
    return { year, month: parseInt(subPart.slice(1)) };
  }

  periodFieldOptions() {
    const options = [['', I18n.t('select_blank_option_default', { scope: 'fe' })]];
    if (this.props.findComponentsCallback) {
      this.props.findComponentsCallback({ type: 'period_field' })
        .filter((c) => c.periodType === 'monthly')
        .forEach((c) => {
          const label = Helpers.getText(c.translations, c.label, this.props.language);
          options.push([c.name, label || c.name]);
        });
    }
    return options;
  }

  renderView() {
    const wrapperProps = {...this.props.content, self: this};

      if (!this.props.content.tool) return <FieldWrapper {...wrapperProps}><Alert level='warning'>{I18n.t('configuration_incomplete')}</Alert></FieldWrapper>

      const manifestItem = EXTERNAL_TOOL_MANIFEST[this.props.content.tool];
      const Component = manifestItem.Component;
      
      if (!this.context.projectId) {
        return <FieldWrapper {...wrapperProps}><Alert level='info'>{I18n.t('no_project_context_info', { scope: 'fe' })}</Alert></FieldWrapper>;
      }

    if (this.props.content.tool === 'work_logs') {
      const rawPeriod = this.props.content.periodFieldName
        ? (this.props.answers || {})[this.props.content.periodFieldName]
        : null;
      const { year, month } = this.parsePeriodAnswer(rawPeriod);

      return (
        <FieldWrapper {...wrapperProps}>
          {(!year || !month) ? (
            <Alert level='info'>{I18n.t('worklog_select_period_info', { scope: 'fe' })}</Alert>
          ) : (
            <Component projectId={this.context.projectId} year={year} month={month} />
          )}
        </FieldWrapper>
      );
    }

    return <FieldWrapper 
      {...wrapperProps} 
      link={
        <TooltipWithRef title={I18n.t('opens_in_new_tab')}>
          <a className='ml-1' href={manifestItem.path(this.context.projectId)}>
            <FaIcon type='arrow-up-right-from-square' style={{marginRight: '0.5rem'}}/>
            {I18n.t('edit')
          }</a>
        </TooltipWithRef>
      }>
      <Component 
            {...manifestItem.defaultProps}
            projectId={this.context.projectId}
          />
    </FieldWrapper>
  }

  renderPrettyView() {
    return (
      <h1>TBD</h1>
    );
  }

  renderSettings() {
    let toolOptions = [...Object.keys(EXTERNAL_TOOL_MANIFEST).map(k => ({ label: I18n.t(`form_external_tool_${k}`, { scope: 'fe' }), key: k }))]
    return (
      <div>
        <Setting.TextField name="label" label={I18n.t('field_label', { scope: 'fe' })} value={this.getText('label')} onChange={this.updateSettings} />
        <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', { scope: 'fe' })} value={this.props.content.label_hidden} onChange={this.updateSettings} />
        <Setting.RadioSelection 
          name="tool" 
          options={toolOptions} 
          label={I18n.t('field_tool', { scope: 'fe' })} 
          selectedOption={this.props.content.tool} 
          onChange={this.updateSettings} 
        />
        {this.props.content.tool === 'work_logs' && (
          <Setting.Dropdown
            name="periodFieldName"
            label={I18n.t('field_period_source', { scope: 'fe' })}
            value={this.props.content.periodFieldName}
            onChange={this.updateSettings}
            description={I18n.t('field_period_source_description', { scope: 'fe' })}
            options={this.periodFieldOptions()}
          />
        )}
        {/* <Setting.TextField name="text" label={I18n.t('field_text', { scope: 'fe' })} value={this.getText('text')} onChange={this.updateSettings} />
        <Setting.TextField name="description" label={I18n.t('field_description', { scope: 'fe' })} value={this.getText('description')} onChange={this.updateSettings} />
        <Setting.Checkbox name="guideline" label={I18n.t('guideline', { scope: 'fe' })} value={this.props.content.guideline} onChange={this.updateSettings} /> */}
      </div>
    );
  }

  beforeUpdateSettings(content) {
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    content.guideline = castDataType(content.guideline, DataType.BOOLEAN);
    this.beforeUpdateSettingsPrettyView(content);
  }
}
